/**
 * DiveDeeperSection — landing page "Dive Deeper" marquee section.
 *
 * Extracted from Landing.js (Jun 2026) to slim the monolith by ~118 lines.
 * Pure presentational component — no props, no state. data-testid + visual
 * markup preserved exactly so Playwright suites don't break.
 *
 * Renders the 9 marquee pills users can click to dive deeper into each
 * surface (patent portfolio, scorecard, about, AI-CFO brief, Auto-Compiler Proxy,
 * refund pledge, free tools, developer hub, SOC2). Arbitrage engine link points
 * to `/engine-27` (the dedicated Real-Time Token Arbitrage page).
 */
import React from 'react';
import { Badge } from '../ui/badge';
import {
  Zap, Trophy, BookOpen, BarChart3, Activity, ShieldCheck,
  Calculator, Terminal, Lock,
} from 'lucide-react';
import DiveDeeperMarquee from '../DiveDeeperMarquee';

const MARQUEE_ITEMS = [
  { to: '/patent-portfolio',         tag: 'Why now',    title: 'First-to-market',        Icon: Zap,         badge: 'bg-emerald-500 shadow-lg shadow-emerald-500/40', iconColor: 'text-white', text: 'text-emerald-300', hover: 'hover:border-emerald-400/40' },
  { to: '/honest-scorecard',         tag: 'The moat',   title: 'Why teams switch',       Icon: Trophy,      badge: 'bg-fuchsia-500 shadow-lg shadow-fuchsia-500/40', iconColor: 'text-white', text: 'text-fuchsia-300', hover: 'hover:border-fuchsia-400/40' },
  { to: '/about',                    tag: 'Manifesto',  title: 'The CARTIE thesis',      Icon: BookOpen,    badge: 'bg-sky-500 shadow-lg shadow-sky-500/40',         iconColor: 'text-white', text: 'text-sky-300',     hover: 'hover:border-sky-400/40' },
  { to: '/ai-cfo',                   tag: 'For CFOs',   title: 'AI CFO weekly brief',    Icon: BarChart3,   badge: 'bg-violet-500 shadow-lg shadow-violet-500/40',   iconColor: 'text-white', text: 'text-violet-300',  hover: 'hover:border-violet-400/40' },
  // Arbitrage engine link → the new dedicated /engine-27 page (was /token-intelligence-suite).
  { to: '/engine-27',                tag: 'Auto-Compiler', title: 'Token arbitrage · live', Icon: Activity,    badge: 'bg-green-500 shadow-lg shadow-green-500/40',     iconColor: 'text-white', text: 'text-green-300',   hover: 'hover:border-green-400/40' },
  { to: '/refund',                   tag: 'Pricing',    title: '3× refund pledge',       Icon: ShieldCheck, badge: 'bg-rose-500 shadow-lg shadow-rose-500/40',       iconColor: 'text-white', text: 'text-rose-300',    hover: 'hover:border-rose-400/40' },
  { to: '/free-tools',               tag: 'Free tools', title: '7 free calculators',     Icon: Calculator,  badge: 'bg-amber-500 shadow-lg shadow-amber-500/40',     iconColor: 'text-white', text: 'text-amber-300',   hover: 'hover:border-amber-400/40' },
  { to: '/developers',               tag: 'For devs',   title: 'Developer Hub + SDK',    Icon: Terminal,    badge: 'bg-pink-500 shadow-lg shadow-pink-500/40',       iconColor: 'text-white', text: 'text-pink-300',    hover: 'hover:border-pink-400/40' },
  { to: '/soc2',                     tag: 'Security',   title: 'SOC 2 + Trust',          Icon: Lock,        badge: 'bg-teal-500 shadow-lg shadow-teal-500/40',       iconColor: 'text-white', text: 'text-teal-300',    hover: 'hover:border-teal-400/40' },
];

export default function DiveDeeperSection() {
  return (
    <section className="py-12 relative overflow-hidden border-t border-white/5" data-testid="landing-dive-deeper">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/4 top-0 w-[600px] h-[400px] rounded-full bg-emerald-500/[0.04] blur-[120px]" />
        <div className="absolute right-1/4 bottom-0 w-[500px] h-[400px] rounded-full bg-fuchsia-500/[0.04] blur-[120px]" />
      </div>
      <div className="relative px-4">
        <div className="max-w-6xl mx-auto text-center mb-5">
          <Badge className="mb-3 bg-white/5 text-slate-300 border-white/10 tracking-wider font-mono">
            DIVE DEEPER
          </Badge>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.015em] leading-[1.15] text-white">
            The <span className="text-emerald-400">full story</span> in the depth you want.
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto mt-2 text-sm">
            Hover to pause · click any pill to dive into that page.
          </p>
        </div>
      </div>

      <DiveDeeperMarquee items={MARQUEE_ITEMS} />
    </section>
  );
}
